import React, { useState, useMemo } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";

export interface PasswordInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
}

// Counter for generating stable IDs when id prop is not provided
let passwordInputIdCounter = 0;

export const PasswordInput = React.forwardRef<
  HTMLInputElement,
  PasswordInputProps
>(({ label, error, helperText, fullWidth = false, className = "", id, ...props }, ref) => {
  const [showPassword, setShowPassword] = useState(false);

  // Generate stable ID only once using useMemo
  const inputId = useMemo(() => {
    if (id) return id;
    // Use a stable counter-based ID instead of random
    return `password-input-${++passwordInputIdCounter}`;
  }, [id]);

  const hasError = !!error;

  const inputClasses = `
    block w-full px-4 py-3 pr-10 border 
    focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all
    disabled:opacity-50 disabled:cursor-not-allowed
    ${hasError
      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
      : "border-store-accent-light focus:border-store-accent focus:ring-store-accent"
    }
    ${fullWidth ? "w-full" : ""}
    ${className}
  `.trim().replace(/\s+/g, " ");

  const togglePasswordVisibility = (): void => {
    setShowPassword((prev) => !prev);
  };

  return (
    <div className={fullWidth ? "w-full" : ""}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-semibold text-store-primary mb-2"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          type={showPassword ? "text" : "password"}
          className={inputClasses}
          aria-invalid={hasError}
          aria-describedby={
            error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
          }
          {...props}
        />
        <button
          type="button"
          onClick={togglePasswordVisibility}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-store-primary-light hover:text-store-primary transition-colors focus:outline-none focus:ring-2 focus:ring-store-accent focus:ring-opacity-30 rounded p-1"
          aria-label={showPassword ? "Hide password" : "Show password"}
          tabIndex={-1}
        >
          {showPassword ? (
            <FaEyeSlash className="w-5 h-5" />
          ) : (
            <FaEye className="w-5 h-5" />
          )}
        </button>
      </div>
      {error && (
        <p id={`${inputId}-error`} className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      {helperText && !error && (
        <p id={`${inputId}-helper`} className="mt-1 text-sm text-store-primary-light">
          {helperText}
        </p>
      )}
    </div>
  );
});

PasswordInput.displayName = "PasswordInput";

export default PasswordInput;

